import type React from 'react';
import { useContext } from 'react';

import styled from '@emotion/styled';

import { type TypedDocumentNode, gql } from '@apollo/client';
import { useSuspenseQuery } from '@apollo/client/react';
import { useTranslations } from 'next-intl';

import { transientOptions } from '@common/themes/styles/styled';

import type {
  ActionHightlightListQuery,
  ActionHightlightListQueryVariables,
  PlanContextFragment,
} from '@/common/__generated__/graphql';
import { getActionTermContext } from '@/common/i18n';
import images, { getActionImage } from '@/common/images';
import { ActionListLink } from '@/common/links';
import Button from '@/components/common/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { Col, type ColProps, Row } from '@/components/common/layout/LayoutGrid';
import { getReadableThemeTextColor } from '@/components/contentblocks/colorUtils';
import EmbedContext from '@/context/embed';

import Icon from '../common/Icon';
import ActionHighlightCard from './ActionHighlightCard';

export const GET_ACTION_LIST: TypedDocumentNode<
  ActionHightlightListQuery,
  ActionHightlightListQueryVariables
> = gql`
  query ActionHightlightList($plan: ID!, $first: Int!, $orderBy: String!) {
    planActions(plan: $plan, first: $first, orderBy: $orderBy) {
      id
      identifier
      name(hyphenated: true)
      officialName
      completion
      updatedAt
      image {
        ...CardImage
      }
      plan {
        id
      }
      color
      status {
        id
        identifier
        name
        color
      }
      statusSummary {
        identifier
      }
      implementationPhase {
        id
        name
        identifier
      }
      categories {
        id
        image {
          ...CardImage
        }
        parent {
          id
          image {
            ...CardImage
          }
          parent {
            id
            image {
              ...CardImage
            }
          }
        }
      }
    }
  }
  ${images.fragments.cardImage}
`;

const ListHeader = styled(Col, transientOptions)<{
  $embed?: { active: boolean };
}>`
  h2 {
    text-align: center;
    padding: 0;
    color: ${(props) =>
      getReadableThemeTextColor(
        props.$embed?.active ? props.theme.themeColors.white : props.theme.neutralLight,
        props.theme.headingsColor,
        props.theme.themeColors.white
      )};
    margin-bottom: ${(props) => props.theme.spaces.s300};
    font-size: ${(props) => props.theme.fontSizeLg};

    ${(props) => props.theme.breakpoints.up('md')} {
      font-size: ${(props) => props.theme.fontSizeXl};
    }
  }
`;

const ReactStrapCol = (props: React.PropsWithChildren<ColProps>) => {
  const childProps = Object.assign(
    // remove the embed prop so it won't end up as a DOM attribute
    {},
    props,
    { embed: undefined, children: undefined }
  );
  return <Col {...childProps}>{props.children}</Col>;
};

const StyledCardContainer = styled(ReactStrapCol, transientOptions)<{
  $embed?: { active: boolean };
}>`
  margin-bottom: ${(props) => props.theme.spaces.s150};
  ${(props) => (props.$embed?.active ? '' : 'transition: all 0.5s ease;')}

  .card {
    height: 100%;
  }
`;

/* Rendered card width in the xs=12, md=6, lg=4 highlight grid */
const HIGHLIGHT_GRID_IMAGE_SIZES =
  '(min-width: 1400px) 434px, (min-width: 1200px) 372px, (min-width: 768px) calc(50vw - 26px), 100vw';

export type ActionHighlightListAction = NonNullable<
  NonNullable<ActionHightlightListQuery['planActions']>[number]
>;

type ActionHighlightsCardListProps = {
  actions: ActionHighlightListAction[];
  plan: PlanContextFragment;
  displayHeader?: boolean;
};

function ActionHighlightsCardList(props: ActionHighlightsCardListProps) {
  const t = useTranslations();
  const { actions, plan, displayHeader } = props;
  // Components which use the EmbedContext support embedding
  const embed = useContext(EmbedContext);

  return (
    <Row>
      {displayHeader && (
        <ListHeader xs="12" $embed={embed}>
          <h2>{t('recently-updated-actions', getActionTermContext(plan) || {})}</h2>
        </ListHeader>
      )}
      {actions?.map((item) => (
        <StyledCardContainer
          xs="12"
          md="6"
          lg="4"
          key={item.id}
          className="d-flex align-items-stretch"
          $embed={embed}
        >
          <ActionHighlightCard
            action={item}
            image={getActionImage(plan, item)}
            imageSizes={HIGHLIGHT_GRID_IMAGE_SIZES}
            hideIdentifier={plan.hideActionIdentifiers || false}
          />
        </StyledCardContainer>
      ))}
      {!embed.active && (
        <Col xs="12" className="mt-5 mb-5">
          <ActionListLink>
            <Button color="primary">
              {t('see-all-actions', getActionTermContext(plan) || {})} <Icon.ArrowRight />
            </Button>
          </ActionListLink>
        </Col>
      )}
    </Row>
  );
}

type ActionHighlightsListProps = {
  plan: PlanContextFragment;
  count?: number;
  displayHeader?: boolean;
};

function ActionHighlightsList(props: ActionHighlightsListProps) {
  const { plan, count, displayHeader } = props;
  const t = useTranslations();

  const queryParams = {
    plan: plan.identifier,
    first: count ?? 6,
    orderBy: '-updatedAt',
  };

  const { data, error } = useSuspenseQuery(GET_ACTION_LIST, {
    variables: queryParams,
  });

  if (error || !data.planActions)
    return (
      <ErrorMessage
        message={t('error-loading-actions', getActionTermContext(plan))}
        details={error?.message}
      />
    );

  return (
    <ActionHighlightsCardList
      actions={data.planActions}
      plan={plan}
      displayHeader={displayHeader ?? true}
    />
  );
}

export default ActionHighlightsList;
